import { useCallback, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import config from "../config";

const SUPPORTED = ["en", "fr", "es", "sw"];

// Normalize any locale code (e.g. "en-US") to a supported short code
const normalize = (lng) => {
  if (!lng) return "en";
  const short = lng.split("-")[0];
  return SUPPORTED.includes(short) ? short : "en";
};

export function useLanguage() {
  const { i18n } = useTranslation();
  const [currentLang, setCurrentLang] = useState(() => normalize(i18n.resolvedLanguage || i18n.language));

  // Stay in sync with i18next language changes (e.g. after login applyUserLanguage)
  useEffect(() => {
    const onChanged = (lng) => setCurrentLang(normalize(lng));
    i18n.on("languageChanged", onChanged);
    return () => i18n.off("languageChanged", onChanged);
  }, [i18n]);

  // Called immediately after login — syncs the language stored in Neo4j into the UI
  const applyUserLanguage = useCallback((uiLanguage) => {
    const lang = SUPPORTED.includes(uiLanguage) ? uiLanguage : "en";
    i18n.changeLanguage(lang);
    localStorage.setItem("kin_ui_lang", lang);
    document.documentElement.lang = lang;
  }, [i18n]);

  // Called by LanguageSwitcher — updates UI instantly and persists to Neo4j
  const changeLanguage = useCallback(async (lang, id, role) => {
    if (!SUPPORTED.includes(lang)) return;

    await i18n.changeLanguage(lang);
    localStorage.setItem("kin_ui_lang", lang);
    document.documentElement.lang = lang;

    // Fire-and-forget — localStorage is the fast path, Neo4j is the durable store
    if (id) {
      axios
        .post(`${config.API_BASE_URL}/user/language`, { id, role, language: lang })
        .catch(() => {});
    }
  }, [i18n]);

  return {
    currentLang,
    changeLanguage,
    applyUserLanguage,
    SUPPORTED
  };
}
